import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  authApi,
  adminApi,
  refreshAccessToken,
  getLastRefreshFailure,
  isTransientRefreshFailure,
  loginRedirectUrl,
  tokenExpiryFields,
  LogoutReason,
} from '../services/api';

interface User {
  user_id: string;
  username?: string;
  email: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  company?: string;
  role?: string;
  is_admin?: boolean;
  is_active?: boolean;
  avatar_url?: string;
  job_title?: string;
  setup_completed?: boolean;
  subscription_plan?: string;
  subscription_status?: string;
  max_products?: number;
  trial_end_date?: string;
  subscription_end_date?: string;
  country?: string;
  impersonated_by?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  /** [KAYIT-GIRIS] sayfa degisince onceki sayfanin hata metni tasinmasin (kayit 409 -> giris kutusu) */
  clearError?: () => void;
  login: (credentials: { username: string; password: string }) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: (reason?: LogoutReason) => void;
  updateUser:(userData: Partial<User>) => void;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: false,
  error: null,
  clearError: () => {},
  login: async () => {},
  register: async () => {},
  logout: () => {},
  updateUser: () => {},
  isAuthenticated: false,
  isImpersonating: false,
  impersonate: async () => {},
  exitImpersonation: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // [AVATAR-ME] updates that arrive before the user is loaded (e.g. profile page vs /me race); applied on load
  const pendingUpdates = useRef<Partial<User>>({});

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const authUser = localStorage.getItem('authUser');
        if (authUser) {
          const parsed = JSON.parse(authUser);
          const savedToken = parsed?.access_token || parsed?.token;

          if (savedToken) {
            setToken(savedToken);

            // Fetch latest user data from /me endpoint to sync with database
            try {
              const meData = await authApi.me();

              // Update localStorage with fresh user data. Re-read first: a refresh may have stored
              // new tokens while /me was pending, and the snapshot taken above would overwrite them.
              const latestAuthUser = localStorage.getItem('authUser');
              const latest = latestAuthUser ? JSON.parse(latestAuthUser) : null;
              // [AVATAR-ME] merge, don't replace: fields /me does not return are kept; pending updates win
              const merged = { ...(latest?.user || parsed?.user || {}), ...meData, ...pendingUpdates.current };
              pendingUpdates.current = {};
              setUser(merged);
              if (latest) {
                latest.user = merged;
                localStorage.setItem('authUser', JSON.stringify(latest));
              }
            } catch (meError) {
              // /me request failed → fallback to localStorage
              console.warn('[Auth] /me failed, using localStorage:', meError);
              if (parsed?.user) {
                setUser({ ...parsed.user, ...pendingUpdates.current });
                pendingUpdates.current = {};
              }
            }
          }
        }
      } catch (e) {
        console.error('Failed to restore auth session:', e);
        localStorage.removeItem('authUser');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (credentials: { username: string; password: string }) => {
    try {
      // [KAYIT-HATA] (17 Eki 2026) genel `loading` YALNIZ acilistaki oturum yuklemesi icin: PublicRoute loading'de sayfayi
      // spinner'la DEGISTIRIYOR -> istek sirasinda form unmount olup hata kutusu ve girilen bilgiler siliniyordu.
      // Gonderim durumunu form kendisi tutar (isSubmitting / yerel state).
      setError(null);

      // Real API call - send email (username field contains email)
      const response = await authApi.login({
        email: credentials.username,
        password: credentials.password,
      });

      // Backend returns: { access_token, refresh_token, token_type, user }
      if (response.access_token && response.user) {
        const authData = {
          token: response.access_token,
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          user: response.user,
          expires_in: response.expires_in || 604800,
          token_expires_at: Date.now() + ((response.expires_in || 604800) * 1000),
        };

        // Save to session storage
        localStorage.setItem('authUser', JSON.stringify(authData));

        // DON'T update state before redirect - prevents flash
        // State will be restored from localStorage on page load

        // Redirect based on role and setup status
        if (response.user.role === 'admin' || response.user.is_admin) {
          window.location.href = '/admin/dashboard';
        } else if (!response.user.setup_completed) {
          // New users go to setup wizard
          window.location.href = '/setup';
        } else {
          window.location.href = '/dashboard';
        }
        return;
      } else {
        throw new Error('Login failed - invalid response');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      throw err;
    }
  };

  // Register function
  const register = async (userData: any) => {
    try {
      // [KAYIT-HATA] genel loading'e dokunulmaz (login'deki not) — hata sonrasi form ve mesaj yerinde kalir
      setError(null);

      const response = await authApi.register(userData);

      // Backend returns: { access_token, refresh_token, token_type, expires_in, user }
      const token = response.access_token || response.token;
      const refreshToken = response.refresh_token;
      const user = response.user;

      if (token && user) {
        const authData = {
          token: token,
          access_token: token,
          refresh_token: refreshToken,
          user: user,
          expires_in: response.expires_in || 604800,
          token_expires_at: Date.now() + ((response.expires_in || 604800) * 1000),
        };

        // Save to session storage
        localStorage.setItem('authUser', JSON.stringify(authData));

        // Update state
        setToken(token);
        setUser(user);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      throw err;
    }
  };

  // Logout function
  const logout = (reason: LogoutReason = 'user_logout') => {
    // Clear both normal and impersonation storage
    localStorage.removeItem('authUser');
    localStorage.removeItem('adminOriginalAuth');
    pendingUpdates.current = {};

    // Redirect immediately - don't clear state to prevent flash
    // State will be cleared on page reload since localStorage is empty
    window.location.href = loginRedirectUrl(reason);
  };

  // Impersonate user — admin switches to a user's perspective
  const impersonate = async (userId: string) => {
    try {
      const response = await adminApi.impersonate(userId);

      if (response.success && response.access_token && response.user) {
        // Save admin's current auth data for later restoration
        const currentAuth = localStorage.getItem('authUser');
        if (currentAuth) {
          localStorage.setItem('adminOriginalAuth', currentAuth);
        }

        // Switch to impersonated user's token (no refresh_token — 60min only)
        const impersonateData = {
          token: response.access_token,
          access_token: response.access_token,
          refresh_token: null,
          user: response.user,
          ...tokenExpiryFields(response.expires_in),
        };
        localStorage.setItem('authUser', JSON.stringify(impersonateData));

        // Redirect to user's dashboard
        window.location.href = '/dashboard';
      } else {
        throw new Error(response.error || 'Impersonation failed');
      }
    } catch (err: any) {
      throw err;
    }
  };

  // Exit impersonation — restore admin's original session
  const exitImpersonation = () => {
    const adminAuth = localStorage.getItem('adminOriginalAuth');
    if (adminAuth) {
      localStorage.setItem('authUser', adminAuth);
      localStorage.removeItem('adminOriginalAuth');
      window.location.href = '/admin/dashboard';
    } else {
      // Fallback: no admin auth found, just logout
      logout('impersonation_exit');
    }
  };

  // Update user function - for profile updates
  const updateUser = (userData: Partial<User>) => {
    // [AVATAR-ME] functional update: a caller holding a stale `user` (null before /me) no longer drops the update;
    // before load it is kept and applied when /me returns. Side effects below are idempotent (StrictMode-safe).
    setUser((prev) => {
      if (!prev) {
        pendingUpdates.current = { ...pendingUpdates.current, ...userData };
        return prev;
      }

      // Merge new data with existing user
      const updatedUser = { ...prev, ...userData };

      // Update localStorage
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        try {
          const parsed = JSON.parse(authUser);
          parsed.user = updatedUser;
          localStorage.setItem('authUser', JSON.stringify(parsed));
        } catch (e) {
          console.error('Failed to update auth session:', e);
        }
      }
      return updatedUser;
    });
  };

  const isImpersonating = !!user?.impersonated_by || !!localStorage.getItem('adminOriginalAuth');

  // Proactive token refresh — expire olmadan 5 dakika önce background'da refresh
  useEffect(() => {
    if (!token || !user) return;

    const checkTokenExpiry = async () => {
      try {
        const authUser = localStorage.getItem('authUser');
        if (!authUser) return;

        const parsed = JSON.parse(authUser);
        const expiresAt = parsed.token_expires_at;
        if (!expiresAt) return;

        const timeLeft = expiresAt - Date.now();

        // 5 dakika (300000ms) kaldıysa ve hala pozitifse refresh yap
        if (timeLeft > 0 && timeLeft < 5 * 60 * 1000) {
          console.log('[Auth] Token expiring soon, proactive refresh...');
          const newToken = await refreshAccessToken();
          if (newToken) {
            console.log('[Auth] Proactive refresh successful');
            // AuthContext state'ini de güncelle
            setToken(newToken);
          } else if (isTransientRefreshFailure(getLastRefreshFailure())) {
            // 429 / 5xx / network: keep the session; the next check (60 s) or a request's 401 tries again
            console.warn(`[Auth] Proactive refresh transient failure (${getLastRefreshFailure()}), session kept`);
          } else {
            console.warn('[Auth] Proactive refresh failed, logging out');
            logout(getLastRefreshFailure());
          }
        }
      } catch (e) {
        // Proactive refresh hatası kritik değil — reactive refresh yedek olarak var
        console.warn('[Auth] Proactive refresh check error:', e);
      }
    };

    // Her 60 saniyede kontrol et
    const interval = setInterval(checkTokenExpiry, 60 * 1000);

    // İlk mount'ta da bir kez kontrol et
    checkTokenExpiry();

    return () => clearInterval(interval);
  }, [token, user]);

  const value = {
    user,
    token,
    loading,
    error,
    clearError: () => setError(null),
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!token && !!user,
    isImpersonating,
    impersonate,
    exitImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
